import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { ArrowLeft, User, Phone, Mail, Download } from "lucide-react";

export default function ConnectorReportDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/reports/connector/${id}`).then((r) => setData(r.data)); }, [id]);
  if (!data) return <div>Loading…</div>;
  const { connector, kpi, leads } = data;

  const downloadExcel = () => {
    const headers = ["Lead #", "Client Name", "Mobile", "Service", "Status", "Assigned To", "Added On", "Updated On"];
    const rows = (leads || []).map((l) => [
      l.lead_number,
      l.client_name,
      l.phone,
      l.service_type,
      l.status,
      l.assigned_to_name || "",
      new Date(l.created_at).toLocaleDateString(),
      new Date(l.updated_at).toLocaleDateString(),
    ]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeName = (connector.name || "connector").replace(/[^A-Za-z0-9_-]+/g, "_");
    a.href = url;
    a.download = `${safeName}_clients_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="connector-report-detail-page">
      <Link to="/reports/connectors" className="text-sm link-blue inline-flex items-center gap-1"><ArrowLeft size={14} /> Back</Link>
      <div className="card p-6 flex items-start gap-4 flex-wrap">
        <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center font-heading font-extrabold text-2xl">{connector.name[0]}</div>
        <div>
          <h1 className="font-heading text-3xl font-extrabold">{connector.name}</h1>
          <div className="text-sm text-muted-foreground font-mono">@{connector.username}</div>
          <div className="mt-2 grid sm:grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2"><Phone size={14} className="text-muted-foreground" /> {connector.phone || "—"}</div>
            <div className="flex items-center gap-2"><Mail size={14} className="text-muted-foreground" /> {connector.email || "—"}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        {[
          ["Total Cases", kpi.total_leads],
          ["Approved", kpi.approved],
          ["Rejected", kpi.rejected],
          ["Pending", kpi.pending],
          ["In Process", kpi.in_process],
          ["Business", `₹${kpi.total_business.toFixed(2)}`],
          ["Commission", `₹${kpi.commission_earned.toFixed(2)}`],
        ].map(([k, v]) => (
          <div key={k} className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{k}</div><div className="mt-2 font-heading text-2xl font-extrabold tabular">{v}</div></div>
        ))}
      </div>

      <div className="card overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div className="font-semibold">Client Details</div>
          <button
            onClick={downloadExcel}
            disabled={!leads || leads.length === 0}
            className="btn btn-primary !py-1.5 !text-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="btn-download-connector-clients-excel"
          >
            <Download size={14} /> Download Excel
          </button>
        </div>
        <table className="table-clean">
          <thead><tr><th>Lead</th><th>Client</th><th>Mobile</th><th>Service</th><th>Status</th><th>Assigned</th><th>Added</th><th>Updated</th></tr></thead>
          <tbody>
            {leads.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-8">No leads</td></tr>}
            {leads.map((l) => (
              <tr key={l.id}>
                <td><Link to={`/leads/${l.id}`} className="font-mono link-blue">{l.lead_number}</Link></td>
                <td className="font-semibold">{l.client_name}</td>
                <td className="tabular">{l.phone}</td>
                <td><span className="badge badge-blue">{l.service_type}</span></td>
                <td><StatusBadge status={l.status} /></td>
                <td>{l.assigned_to_name || <span className="text-muted-foreground text-xs">—</span>}</td>
                <td className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="text-xs text-muted-foreground">{new Date(l.updated_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
